import React, {useState, useEffect} from "react";
import {useAuthWithRoute, useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {Button, Card, Col, Row, Table} from "reactstrap";
import {VsiBubble, VsiH4, VsiH6, VsiLgText, VsiSpanText} from "../../../styled/App.styled";
import {useVsiAppContext} from "../../../stores/VsiApp";
import Stockchart from "../../../components/Stockchart";
import {useNavigate} from "react-router-dom";
import {
    applyGrowthRateToNext,
    formatDualAxisStockChartConfig,
    formatStockChartConfig,
    simpleGrowthRate,
    simpleForecast
} from "../../../utils/Helpers";
import dayjs from "dayjs";
import {forecastData, weightedGrowthRate} from "../../../utils/growthRateForecastUtils";

const fieldsObjToGrowthRate = {
    pool_used: "pool_used",
    capacity: "capacity",
    subscription_capacity: "subscription_capacity",
    order_capacity: "order_capacity"
};

const allClusterTypes = [
    {
        name: "vcpu",
        label: "vCPU",
        chartDefs: [
            {name: "Virtual CPU Used", field: "total_virtual_cpus_used", grField: "cpu_used_growth"},
            {name: "Virtual CPU Capacity", field: "total_virtual_cpus", grField: "cpu_capacity_growth"},
            {name: "Virtual CPUs Allocated", field: "total_virtual_cpus_alloc", grField: "cpu_capacity_growth"},
        ],
    },
    {
        name: "memory",
        label: "Memory",
        chartDefs: [
            {name: "Memory Used (GiB)", field: "memory_used", grField: "memory_used_growth"},
            {name: "Memory Capacity (GiB)", field: "total_memory", grField: "memory_capacity_growth"},
        ],
    },
    {
        name: "disk",
        label: "Disk",
        chartDefs: [
            {name: "Disk Used (GiB)", field: "used", grField: "disk_used_growth"},
            {name: "Disk Capacity (GiB)", field: "allocated"},
        ],
    },
    {
        name: "vmbuild",
        label: "VM Build",
        chartDefs: [
            {name: "# of VMs", field: "num_vms", grField: "vm_build_growth"},
            {name: "VM Build Capacity", field: "vm_build_capacity", grField: "vm_build_capacity_growth"},
        ],
    },
    {
        name: "cost",
        label: "Cost",
        chartDefs: [
            {name: "Estimated Utilization Cost", field: "utilization_cost"},
            {name: "Cluster Cost", field: "total_cost"},
        ],
    },
];

const DevicePoolTr = ({theme, array, index, deviceData, onUnitClick}) => {

    const [displayPools, setDisplayPools] = useState(false);

    return (
        <>
            <tr className={`table-${theme}`} key={`${array}_${index}_pools_with`}>
                <th>
                    <VsiSpanText style={{cursor: "pointer"}} onClick={() => setDisplayPools(!displayPools)}>
                        {displayPools ? <i className="fa-solid fa-minus pe-2"/> :
                            <i className="fa-solid fa-plus pe-2"/>}
                    </VsiSpanText>
                    <VsiSpanText>{array}</VsiSpanText>
                </th>
                <td/>
            </tr>
            {(deviceData?.pools ?? [])?.map?.((pool, devIndex) => {
                return (
                    <tr className={`table-${theme} ${displayPools ? "" : "d-none"}`}
                        style={{cursor: "pointer"}}
                        onClick={() => onUnitClick(pool)}
                        key={`${pool.pool_name}_${index}_${devIndex}_num_arrays`}>
                        <td style={{paddingLeft: 50}}>{pool.pool_name}</td>
                        <td style={{textAlign: "right"}}>{pool.weeks_to_max}</td>
                    </tr>
                );
            })}
        </>
    );
};

export default function ForecastSummary({title, context}) {

    let navigate = useNavigate();

    useTitleFromComponent(`${context} / ${title}`);
    const {theme, client, periodId} = useVsiAppContext();

    const [devicesWithPoolsOverForecast, setDevicesWithPoolsOverForecast] = useState({});
    const [activePlan, setActivePlan] = useState({});
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [capacityTrend, setCapacityTrend] = useState([]);
    const [capacityTrendConfig, setCapacityTrendConfig] = useState({});
    const [chartTitle, setChartTitle] = useState('');

    let trendRoute = "";
    let planRoute = "";
    let hostRoute = "";
    let buRoute = "";

    if (selectedUnit && selectedUnit.hasOwnProperty("pool_name")) {
        const encDevice = encodeURIComponent(selectedUnit.device_name);
        const encPool = encodeURIComponent(selectedUnit.pool_name);

        trendRoute = `/api/v6/report/device_pools_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${encDevice}&pool_name=${encPool}`;
        planRoute = `/api/v6/admin/pool_capacity_plan/client/${client.clientId}/${encDevice}/${encPool}`;
        buRoute = `/api/v6/admin/bu_san_capacities/client/${client.clientId}/${encDevice}`;
    } else if (selectedUnit && selectedUnit.hasOwnProperty("cluster_name")) {
        const encVcenter = encodeURIComponent(selectedUnit.vcenter_name);
        const encCluster = encodeURIComponent(selectedUnit.cluster_name);

        trendRoute = `/api/v6/report/virtual_cluster_trend_proc?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encVcenter}&cluster_name=${encCluster}`;
        planRoute = `/api/v6/admin/virtual_cluster_capacity_plan/${client.clientId}/${encVcenter}/${encCluster}`;
        hostRoute = `/api/v6/report/virtual_interactive_host_models_for_client_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${encVcenter}&cluster_name=${encCluster}`;
    }

    // table data
    // const [{recordsets: tableSets}, {isLoading}] = useDataWithRoute(`/api/v6/report/business_unit_over_threshold_proc?client_id=${client.clientId}&period_id=${periodId}`);
    const [{recordsets: tableSets}, {isLoading}] = useDataWithRoute(`/api/v6/report/business_unit_weekly_usage?client_id=${client.clientId}&period_id=${periodId}`);
    const [clustersOverThreshold, setClustersOverThreshold] = useState([]);
    const [{recordset: trendData}, {isLoading: isTrendLoading}] = useDataWithRoute(trendRoute, trendRoute && !!selectedUnit);
    // saved capacity plan data
    const [planData, {isLoading: isPlanLoading}] = useAuthWithRoute(planRoute, planRoute && !!selectedUnit);

    // only for cluster selection; cluster modeling uses a selection of esx hosts
    const [{recordset: esxHosts}, {isLoading: isEsxHostsLoading}] = useDataWithRoute(hostRoute, hostRoute && !!selectedUnit && selectedUnit.hasOwnProperty("cluster_name"));
    // only really for pool
    const [buData, {isLoading: isBuLoading}] = useAuthWithRoute(buRoute, buRoute && !!selectedUnit && selectedUnit.hasOwnProperty("pool_name"));

    const onUnitClick = unit => {
        if (unit?.hasOwnProperty('cluster_name')) {
            setChartTitle(`${unit?.cluster_name} VM Build (${unit?.vcenter_name})`);
        } else if (unit?.hasOwnProperty('pool_name')) {
            setChartTitle(`${unit?.pool_name} Capacity (${unit?.device_name})`);
        }
        setSelectedUnit(unit);
    };

    const onGoToPlanClick = () => {
        if (selectedUnit.hasOwnProperty('pool_name')) {
            navigate(`/reports/storage/device/pool-capacity-plan?device_name=${encodeURIComponent(selectedUnit.device_name)}&pool_name=${encodeURIComponent(selectedUnit.pool_name)}`);
        } else if (selectedUnit.hasOwnProperty('cluster_name')) {
            navigate(`/reports/virtual/cluster/cluster-modeling?cluster_name=${encodeURIComponent(selectedUnit.cluster_name)}&vcenter_name=${encodeURIComponent(selectedUnit.vcenter_name)}&chart_type=vmbuild`)
        }
    }

    const calculateHostCostWithTrendData = (hosts, datum) => {
        let totalCost = 0;
        let utilCost = 0;
        hosts.forEach(host => {

            totalCost += datum.total_virtual_cpus * (host.cost_per_cpu * host.model_count);
            totalCost += datum.total_memory * (host.cost_per_memory * host.model_count);
            totalCost += datum.allocated * (host.cost_per_disk * host.model_count);

            utilCost += datum.total_virtual_cpus_used * host.cost_per_cpu;
            utilCost += datum.used * host.cost_per_disk;
            utilCost += datum.memory_used * host.cost_per_memory;
        });

        return {
            total_cost: totalCost,
            utilization_cost: utilCost,
        }
    }

    const handlePoolCapacityPlan = (data) => {
        const capacity = {};
        const fieldsArr = [...Object.keys(fieldsObjToGrowthRate), "total_cost"];
        const metricFields = {
            capacity: "capacity",
            workload: "pool_used"
        };

        let flattenedCapacityValue = 0;
        let growthFlag = false;
        let growthNum = 0;
        let growthStartingPoint = 0;
        let growthIndex = 1;


        fieldsArr.forEach((field) => {
            capacity[field] = 0;
        });

        return (data || []).map((datum, index) => {
            const newDatum = {...datum};
            const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");

            const capacityField = metricFields?.capacity;
            const workloadField = metricFields?.workload;
            // growth types (before adding workload)
            if (activePlan?.[dateToCompare]?.growth || growthFlag) {
                growthFlag = true;
                if (!growthNum && activePlan?.[dateToCompare]?.growth) {
                    if (!growthStartingPoint) {
                        growthStartingPoint = data[index - 1][workloadField];
                    }
                    if (activePlan?.[dateToCompare]?.growthType === "gibMonth") {
                        growthNum = parseFloat(activePlan?.[dateToCompare]?.growth);
                    } else if (activePlan?.[dateToCompare]?.growthType === "growth") {
                        growthNum = parseFloat(
                            growthStartingPoint *
                            (parseFloat(activePlan?.[dateToCompare]?.growth) / 100)
                        );
                    }
                }
                newDatum[workloadField] = growthStartingPoint + (growthNum * growthIndex);
                growthIndex++;
            }

            // capacity
            if (activePlan?.[dateToCompare]?.capacity) {
                capacity[capacityField] =
                    capacity[capacityField] +
                    parseFloat(activePlan?.[dateToCompare]?.capacity);
            }

            // workload
            if (activePlan?.[dateToCompare]?.workload) {
                capacity[workloadField] =
                    capacity[workloadField] +
                    parseFloat(activePlan?.[dateToCompare]?.workload);
            }

            fieldsArr.forEach((field) => {
                if (field.toLowerCase().includes("subscription")) {
                    newDatum[field] = parseFloat(newDatum[capacityField] * 0.8);
                } else if (field.toLowerCase().includes("order")) {
                    newDatum[field] = parseFloat(newDatum[capacityField] * 0.7);
                } else {
                    newDatum[field] = newDatum[field] + capacity[field];
                    // newDatum[field] = newDatum[field] + workload[field];
                }
            });

            return newDatum;
        });
    };

    const handlePoolStateWithData = () => {
        const truncatedData = [...trendData];
        let pricePerGB = buData?.["pricePerGB"] ?? 0;

        if (pricePerGB === 0) {
            const lastPoint = truncatedData?.[truncatedData.length - 1] ?? {
                pool_used: 0
            };
            pricePerGB = lastPoint?.pool_used / lastPoint.total_cost;
        }

        const keyedGr = Object.keys(fieldsObjToGrowthRate);
        const grObj = {};
        keyedGr.forEach(rate => {
            grObj[rate] = weightedGrowthRate(truncatedData.map(m => ({
                date: m.collection_date,
                value: m[rate]
            }))) * 30 //monthly;
        });


        const forecastedData = forecastData(
            truncatedData,
            keyedGr,
            grObj,
            12,
            "month"
        );

        const allData = [...truncatedData, ...forecastedData];
        const planData = handlePoolCapacityPlan(allData).map(datum => {
            return {
                ...datum,
                total_cost: !datum.total_cost ? pricePerGB * datum.pool_used : datum.total_cost
            };
        });

        const formattedData = formatDualAxisStockChartConfig(
            planData,
            [
                {name: "Used", field: "pool_used", yAxis: 0},
                {name: "Effective Capacity", field: "capacity", yAxis: 0},
                {name: "80% Threshold", field: "subscription_capacity", yAxis: 0},
                {name: "70% Threshold", field: "order_capacity", yAxis: 0}
            ]);

        setCapacityTrend(
            formattedData.series.map((series) => ({
                ...series,
                zoneAxis: "x",
                zones: [
                    {
                        value: Date.parse(
                            truncatedData?.[truncatedData?.length - 1]?.collection_date?.split?.(
                                "T"
                            )?.[0]
                        ),
                    },
                    {
                        dashStyle: "Dash",
                    },
                ],
            }))
        );
        setCapacityTrendConfig({
            yAxis: [
                {
                    title: {
                        text: "GiB",
                        position: "left",
                    }
                },
                {
                    title: {
                        text: "Dollars",
                    },
                    opposite: true,
                    labels: {format: '${value:,2f}'}
                },
            ],
            plotOptions: {
                series: {
                    animation: false,
                },
            },
        });
    };

    const handleClusterCapacityPlan = (data) => {
        const capacity = {
            cpu: 0,
            mem: 0,
            disk: 0,
            vm: 0,
            cost: 0,
        };
        const workload = {...capacity};
        const dateTrackingMetadata = {
            date: '',
            month: '',
        };

        return (data || []).map((datum) => {
            const newDatum = {...datum};
            const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");

            // no longer trending by month, so we just need to affect the first date of the month. this tracks that
            if (dateTrackingMetadata.month !== dateToCompare) {
                dateTrackingMetadata.month = dateToCompare;
                dateTrackingMetadata.date = datum.collection_date;
            }

            if (typeof activePlan?.[dateToCompare]?.models === "object" && dateTrackingMetadata.date === datum.collection_date) {
                Object.keys(activePlan[dateToCompare].models).forEach(
                    (modelKey) => {
                        const host = activePlan[dateToCompare].models[modelKey];
                        const {
                            cpus,
                            memory_size,
                            disk_size,
                            modelCountAdded,
                            model_count,
                            cost_per_disk,
                            cost_per_cpu,
                            cost_per_memory
                        } = host;

                        const modelsToCount = host.isCustom ? (modelCountAdded + model_count) : modelCountAdded;
                        capacity.cpu = capacity.cpu + parseFloat(cpus * modelsToCount);
                        capacity.mem =
                            capacity.mem + parseFloat(memory_size * modelsToCount);
                        capacity.disk =
                            capacity.disk + parseFloat(disk_size * modelsToCount);
                        capacity.cost =
                            capacity.cost + parseFloat(capacity.mem * cost_per_memory + capacity.disk * cost_per_disk + capacity.cpu * cost_per_cpu);

                    }
                );
            }

            if (
                typeof activePlan?.[dateToCompare]?.customCapacity === "object" && dateTrackingMetadata.date === datum.collection_date
            ) {
                const {customCapacity} = activePlan[dateToCompare];
                // note: might need to calibrate vCPU calculation like the first case, but will see
                capacity.cpu = capacity.cpu + customCapacity.cpu;
                capacity.mem = capacity.mem + customCapacity.mem;
                capacity.disk = capacity.disk + customCapacity.disk;
                // capacity.cost = capacity.cost + customCapacity.cost;
            }

            if (
                typeof activePlan?.[dateToCompare]?.customWorkload === "object" && dateTrackingMetadata.date === datum.collection_date
            ) {
                // do something
                const {customWorkload} = activePlan[dateToCompare];

                // note: might need to calibrate vCPU calculation like the first case, but will see
                workload.cpu = workload.cpu + customWorkload.cpu;
                workload.mem = workload.mem + customWorkload.mem;
                workload.disk = workload.disk + customWorkload.disk;
                workload.vm = workload.vm + customWorkload.vm;
                // workload.cost = workload.cost + (customWorkload.cost ?? 0)
            }

            // capacity
            newDatum.total_virtual_cpus = newDatum.total_virtual_cpus + capacity.cpu;
            newDatum.total_memory = newDatum.total_memory + capacity.mem;
            newDatum.allocated = newDatum.allocated + capacity.disk;
            newDatum.vm_build_capacity = newDatum.vm_build_capacity + capacity.vm;
            newDatum.total_cost = newDatum.total_cost + capacity.cost;

            //workload
            newDatum.total_virtual_cpus_used = Math.max(newDatum.total_virtual_cpus_used + workload.cpu, 0);
            newDatum.memory_used = Math.max(newDatum.memory_used + workload.mem, 0);
            newDatum.used = Math.max(newDatum.used + workload.disk, 0);
            newDatum.num_vms = newDatum.num_vms + workload.vm;

            // calculate build cap based on updated newDatum
            let lowerConstrain = selectedUnit.resource_constraint.toLowerCase();
            if (!lowerConstrain.includes("null")) {
                let constrainKey = null;
                if (lowerConstrain.includes("disk")) {
                    constrainKey = "allocated";
                } else if (lowerConstrain.includes("memory")) {
                    constrainKey = "total_memory";
                } else {
                    constrainKey = "total_virtual_cpus";
                }
            }

            return newDatum;
        });
    };

    const handleClusterStateWithData = () => {
        // I have no idea why allocated isn't making it
        const allFieldsForForecast = ['allocated', 'used', 'num_vms', 'vm_capacity'];
        const allFieldsGrObj = {};
        allClusterTypes.forEach((type) => {
            type.chartDefs.forEach((def) => {
                allFieldsForForecast.push(def.field);
                if (def.grField) {
                    allFieldsGrObj[def.field] = def.grField;
                }
            });
        });

        let clusterData = trendData.filter(f => f.cluster_name === selectedUnit.cluster_name).map((datum) => {
            return {
                ...datum,
                ...calculateHostCostWithTrendData(esxHosts ?? [], datum),
            };
        });


        const grObj = {};
        allFieldsForForecast.forEach(rate => {
            grObj[rate] = weightedGrowthRate(clusterData.map(m => ({
                date: m.collection_date,
                value: m[rate]
            }))) * 30 //monthly;
        });

        const forecastedData = forecastData(clusterData, allFieldsForForecast, grObj, 26);

        const newData = [...clusterData, ...handleClusterCapacityPlan(forecastedData)];

        const withPlanData = (newData).map(val => ({
            ...val,
            vm_build_capacity: Math.round(val.vm_build_capacity),
            num_vms: Math.round(val.num_vms)
        }));

        const formattedData = formatStockChartConfig(
            withPlanData,
            allClusterTypes[3].chartDefs,
        );

        setCapacityTrend(formattedData.series.map((series) => ({
            ...series,
            zoneAxis: "x",
            zones: [
                {
                    value: Date.parse(
                        trendData?.[trendData?.length - 1]?.collection_date?.split?.("T")?.[0]
                    ),
                },
                {
                    dashStyle: "Dash",
                },
            ],
        })));
        setCapacityTrendConfig(null);
    };

    useEffect(() => {
        if (tableSets?.[1]) {
            const uniqueDevices = {};
            (tableSets?.[1].filter(f => f.period_id === periodId) || []).forEach(device => {
                const pool_capacity = device.capacity_gb;
                const pool_threshold = pool_capacity * 0.8;
                const pool_used = device.pool_used;
                const growth_rate = weightedGrowthRate(tableSets?.[1].filter(f => f.device_name === device.device_name
                    && f.pool_name === device.pool_name).map(m => ({
                    date: m.collection_date,
                    value: m.pool_used
                })));

                const days_to_max = growth_rate > 0 ? (pool_threshold - pool_used) / growth_rate : 0;

                if ((growth_rate > 0 && days_to_max < 182 && pool_used < pool_threshold) ||
                    (pool_used >= pool_threshold)) {
                    const weeks_to_max = days_to_max / 7;
                    if (!uniqueDevices[device.device_name]) {
                        uniqueDevices[device.device_name] = {
                            pools: []
                        };
                    }
                    uniqueDevices[device.device_name].pools.push({
                        ...device,
                        weeks_to_max: pool_used >= pool_threshold ? 0 : weeks_to_max > 0 ? weeks_to_max | 0 : 0,
                        growth_rate: growth_rate,
                        days_to_max: pool_used >= pool_threshold ? 0 : days_to_max > 0 ? days_to_max | 0 : 0
                    });
                }
            });
            setDevicesWithPoolsOverForecast(uniqueDevices);
        }
        if (tableSets?.[0]) {
            const uniqueClusters = [];
            (tableSets?.[0]?.filter(f => f.period_id === periodId)).forEach(device => {

                const growth_rate = weightedGrowthRate(tableSets?.[0].filter(f => f.device_name === device.device_name
                    && f.cluster_name === device.cluster_name).map(m => ({
                    date: m.collection_date,
                    value: m.num_vms
                })));

                const days_to_max = device.vm_build_capacity / growth_rate;

                if ((growth_rate > 0 && days_to_max < 182 && device.vm_build_capacity > 0) ||
                    (device.vm_build_capacity <= 0)) {

                    const weeks_to_max = days_to_max / 7;

                    uniqueClusters.push({
                        ...device,
                        weeks_to_max: device.vm_build_capacity <= 0 ? 0 : weeks_to_max > 0 ? weeks_to_max | 0 : 0,
                        growth_rate: growth_rate,
                        days_to_max: device.vm_build_capacity <= 0 ? 0 : days_to_max > 0 ? days_to_max | 0 : 0
                    });

                }


            });
            setClustersOverThreshold(uniqueClusters);

        }
    }, [tableSets]);

    useEffect(() => {
        if (selectedUnit && selectedUnit.hasOwnProperty("pool_name")) {
            const savedData = {...(planData?.[0] ?? {})};
            (planData?.adjustments || []).forEach((datum) => {
                if (!planData.hasOwnProperty(datum.month)) {
                    planData[datum.month] = {...datum};
                }
            });
            delete savedData.adjustments;
            setActivePlan(savedData);
        } else if (selectedUnit && selectedUnit.hasOwnProperty("cluster_name")) {
            const activePlan = {};
            const foundHostModels = {}; // for faster lookups
            if (planData?.[0]?._id) {
                (planData?.[0]?.adjustments ?? []).forEach((adjustment) => {
                    const models = {};
                    if (!activePlan[adjustment.actualDate]) {
                        activePlan[adjustment.actualDate] = {};
                    }
                    activePlan[adjustment.actualDate] = {...adjustment};
                    (adjustment?.models ?? []).forEach((model) => {
                        if (model.model) {
                            if (!models[model.model]) {
                                models[model.model] = {};
                            }
                            if (!foundHostModels[model.model]) {
                                const hostInfo =
                                    (esxHosts ?? []).find(
                                        (host) => host.model === model.model
                                    ) || {};
                                foundHostModels[model.model] = {...hostInfo};
                            }

                            models[model.model] = {
                                ...foundHostModels[model.model],
                                modelCountAdded: model.modelCountAdded
                            };
                        }
                    });
                    activePlan[adjustment.actualDate].models = models;
                });
                setActivePlan(activePlan)
            }
        }

    }, [planData, selectedUnit, esxHosts]);

    useEffect(() => {
        if (trendData?.length > 0 && selectedUnit && selectedUnit.hasOwnProperty("pool_name")) {
            handlePoolStateWithData();
        } else if (trendData?.length > 0 && selectedUnit && selectedUnit.hasOwnProperty("cluster_name")) {
            handleClusterStateWithData();
        }
    }, [activePlan, selectedUnit, trendData, esxHosts, buData]);

    const isChartLoading = isLoading || isTrendLoading || isPlanLoading || isEsxHostsLoading || isBuLoading;

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>{title}</VsiH4>
            <Row>
                <Col xs={12} md={3}>

                    <VsiBubble suppressCollapse>
                        <VsiH6 className={"mt-1"}>Arrays with Pools Forecasted {">"} Threshold (weeks to
                            threshold):</VsiH6>
                        <VsiBubble noAutobot>
                            <Table striped dark={theme === "dark"}>
                                <tbody>
                                {Object.keys(devicesWithPoolsOverForecast)?.map?.((array, index) => {
                                    const deviceData = devicesWithPoolsOverForecast[array];
                                    return <DevicePoolTr key={`device-over-pool-${array}-${index}`}
                                                         deviceData={deviceData}
                                                         onUnitClick={onUnitClick}
                                                         theme={theme}
                                                         array={array}
                                                         index={index}/>;
                                })}
                                </tbody>
                            </Table>
                        </VsiBubble>
                    </VsiBubble>

                    <VsiBubble suppressCollapse>
                        <VsiH6 className={"mt-1"}>Virtual Clusters {">"} Threshold (resource constraint & weeks to
                            threshold):</VsiH6>
                        <VsiBubble noAutobot>
                            <Table striped dark={theme === "dark"}>
                                <tbody>
                                {clustersOverThreshold?.map((cluster, index) => {
                                    return (
                                        <tr className={`table-${theme}`}
                                            style={{cursor: "pointer"}}
                                            onClick={() => onUnitClick(cluster)}
                                            key={`${cluster.cluster_name}_${index}_num_arrays`}>
                                            <th>{cluster.cluster_name}</th>
                                            <td>{cluster.resource_constraint}</td>
                                            <td style={{textAlign: "right"}}>{cluster.weeks_to_max}</td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        </VsiBubble>
                    </VsiBubble>
                </Col>
                <Col xs={12} md={9}>
                    <VsiBubble noAutobot>
                        {!selectedUnit ? (
                            <VsiLgText>* Please select a pool or cluster from the left</VsiLgText>
                        ) : (
                            <Button onClick={onGoToPlanClick} outline={true}>
                                To Capacity Planning
                                <i className={"fa-solid fa-chart-user px-2"}/>
                            </Button>
                        )}
                        <Stockchart
                            series={capacityTrend}
                            config={capacityTrendConfig}
                            isLoading={isChartLoading}
                            title={chartTitle}
                            formatData="{point.y:,.0f}"
                            formatPoint="{series.name}: <b>{point.y:,.0f}</b>"
                        />
                    </VsiBubble>
                </Col>
            </Row>
        </Card>
    );
}
